import Logger from '../lib/logger';
import { fetchVariantGalleryDocument, VARIANT_GALLERY_SELECTOR } from '../lib/variantGallery';

type PurchaseMode = 'subscription' | 'one_time';

/** Strings that depend on the variant and the purchase mode together. */
interface PdpModeText {
	cta: string;
	stickyCta: string;
	stickyPrice: string;
	/** The one-time price struck beside a subscription price; empty for one-time. */
	stickyCompare: string;
	summary: string;
}

/** One entry of the JSON blocks/pdp-purchase.liquid renders. Every string is pre-formatted there. */
interface PdpVariant {
	id: number;
	available: boolean;
	/** null when the variant has no selling plan; the subscribe option is then hidden. */
	sellingPlanId: number | null;
	/** Written into every `[data-pdp-text="<key>"]`. */
	text: Record<string, string>;
	modes: Record<PurchaseMode, PdpModeText>;
}

// Horizon's ThemeEvents.cartError; product-form.js fires it on window for every refused add.
const CART_ERROR_EVENT = 'cart:error';

// The SCSS `pdp-mobile` breakpoint, below Horizon's two columns. Only phones get the sticky bar (on
// desktop the sticky details column's stacking context would trap it beneath the Gorgias widget)
// and the collapsing subscription stack.
const PHONE_MEDIA_QUERY = '(max-width: 749px)';

class PdpPurchase {

	// Vars
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

	NAME: string;
	dom: HTMLElement;
	variants: Map<number, PdpVariant>;
	variantInput: HTMLInputElement;
	sellingPlanInput: HTMLInputElement;
	subscribePanel: HTMLElement | null;
	status: HTMLElement | null;
	sticky: HTMLElement | null;
	cta: HTMLElement | null;
	/** product-form-component; its id is the `sourceId` of the cart errors it raises. */
	formComponent: HTMLElement | null;
	/** Set when product-form.js refuses the in-flight add (limit, duplicate subscription). */
	addRefused: boolean;
	sectionId: string;
	/** This page's product URL, with `view=` when an alternate template rendered it. */
	galleryPageUrl: string;
	/** Parsed, never-inserted galleries per variant id; each insert is a fresh import. */
	galleries: Map<number, Promise<Element | null>>;
	/** The variant the live gallery was rendered for. */
	galleryVariantId: number;
	galleryAbort: AbortController | null;
	/** The format the server rendered: what a URL without ?variant= means. */
	defaultVariantId: string;

	/**
	 * Class constructor
	 */
	constructor(dom: HTMLElement) {

		this.NAME = 'PdpPurchase';

		this.dom = dom;

		const data = JSON.parse(dom.querySelector('[data-pdp-purchase-data]')?.textContent ?? '{}') as {
			variants: PdpVariant[];
		};
		this.variants = new Map(data.variants.map(variant => [variant.id, variant]));

		// Both are rendered unconditionally by the block.
		this.variantInput = dom.querySelector<HTMLInputElement>('[data-pdp-variant-input]')!;
		this.sellingPlanInput = dom.querySelector<HTMLInputElement>('[data-pdp-selling-plan-input]')!;

		this.subscribePanel = dom.querySelector<HTMLElement>('[data-pdp-subscribe]');
		this.status = dom.querySelector<HTMLElement>('[data-pdp-status]');
		this.sticky = dom.querySelector<HTMLElement>('[data-pdp-sticky]');
		this.cta = dom.querySelector<HTMLElement>('.pdp-purchase__form [data-pdp-submit]:not([form])');
		this.formComponent = dom.querySelector<HTMLElement>('product-form-component');
		this.addRefused = false;

		this.sectionId = dom.dataset.sectionId ?? '';
		this.galleryPageUrl = dom.dataset.galleryPageUrl ?? '';
		this.galleries = new Map();
		// Read before render() touches the input: this is the variant the server rendered the gallery for.
		this.galleryVariantId = Number(this.variantInput.value);
		this.defaultVariantId = this.variantInput.value;
		this.galleryAbort = null;
	}

	/**
	 * init - Initialize the buy core
	 */
	init() {
		Logger.info(`${this.NAME} > init`);

		this.dom.addEventListener('change', this.onChange);
		// Capture: product-form.js handles the submit on the component, below this listener.
		this.dom.addEventListener('submit', this.onSubmit, { capture: true });
		this.dom.addEventListener('cart:update', this.onCartUpdate);
		window.addEventListener(CART_ERROR_EVENT, this.onCartError);
		window.addEventListener('popstate', this.onPopState);

		// The radios are autocomplete="off": Chrome restores form state on Back after this module has
		// run, with no change event, so a restored "Buy it once" would sit over a subscription form.
		this.render({ announce: false });
		this.updateGallery();

		this.initSticky();
		this.initGalleryPrefetch();
	}

	/**
	 * selectedVariant - The variant whose format card is checked
	 */
	selectedVariant(): PdpVariant | undefined {
		const input = this.dom.querySelector<HTMLInputElement>('[data-pdp-format]:checked');
		return input ? this.variants.get(Number(input.value)) : undefined;
	}

	/**
	 * selectedMode - The checked purchase option, forced to one-time when the variant has no plan
	 */
	selectedMode(variant: PdpVariant): PurchaseMode {
		if (variant.sellingPlanId === null) return 'one_time';

		const input = this.dom.querySelector<HTMLInputElement>('[data-pdp-plan]:checked');
		return input?.value === 'one_time' ? 'one_time' : 'subscription';
	}

	onChange = (event: Event) => {
		const target = event.target;
		if (!(target instanceof HTMLInputElement)) return;

		if (target.matches('[data-pdp-format]')) {
			this.render({ announce: true });
			this.syncUrl();
			this.updateGallery();
		} else if (target.matches('[data-pdp-plan]')) {
			this.renderKeepingPlanOptionsBottom(target);
		}
	};

	/**
	 * renderKeepingPlanOptionsBottom - Render a plan change, holding the options' bottom edge still
	 *
	 * Choosing Buy it once on a phone collapses the subscription's stack above it, which would
	 * yank the option up under the shopper's thumb. One fixed anchor (just above the CTA) for both
	 * choices, so toggling back and forth doesn't walk up the page. Safari has no native scroll
	 * anchoring. Works because the stacks follow render()'s mode attribute, not :checked.
	 */
	renderKeepingPlanOptionsBottom(input: HTMLInputElement) {
		const options = this.dom.querySelector('.pdp-purchase__options');
		if (!options || !window.matchMedia(PHONE_MEDIA_QUERY).matches) {
			this.render({ announce: true });
			return;
		}

		const before = options.getBoundingClientRect().bottom;
		this.render({ announce: true });
		const shift = options.getBoundingClientRect().bottom - before;
		// Instant: the page sets smooth scrolling, which would visibly slide the correction.
		if (shift !== 0) window.scrollBy({ top: shift, behavior: 'instant' });
		// Holding the bottom pushes Subscribe up as its stack opens; keep the chosen radio on screen.
		input.scrollIntoView({ block: 'nearest', behavior: 'instant' });
	}

	/**
	 * render - Point the form and every price/label at the selected variant and mode
	 */
	render({ announce }: { announce: boolean }) {
		const variant = this.selectedVariant();
		if (!variant) return;

		const mode = this.selectedMode(variant);
		const modeText = variant.modes[mode];

		this.variantInput.value = String(variant.id);
		this.sellingPlanInput.value = variant.sellingPlanId === null ? '' : String(variant.sellingPlanId);
		this.sellingPlanInput.disabled = mode === 'one_time';
		// Drives the value stacks' show/hide; see renderKeepingPlanOptionsBottom.
		this.dom.dataset.pdpSelectedMode = mode;

		if (this.subscribePanel) this.subscribePanel.hidden = variant.sellingPlanId === null;
		const oneTimeInput = this.dom.querySelector<HTMLInputElement>('[data-pdp-plan][value="one_time"]');
		if (mode === 'one_time' && oneTimeInput) oneTimeInput.checked = true;

		this.dom.querySelectorAll<HTMLElement>('[data-pdp-text]').forEach(element => {
			setText(element, variant.text[element.dataset.pdpText ?? '']);
		});
		this.dom.querySelectorAll<HTMLElement>('[data-pdp-mode-text]').forEach(element => {
			setText(element, modeText[(element.dataset.pdpModeText ?? '') as keyof PdpModeText]);
		});
		this.dom.querySelectorAll<HTMLButtonElement>('[data-pdp-submit]').forEach(button => {
			button.disabled = !variant.available;
		});

		// pdp-heading is a sibling block anywhere in the section; it tags its title for us.
		const title = this.dom.closest('.shopify-section')?.querySelector<HTMLElement>('[data-pdp-title]');
		if (title) setText(title, variant.text.displayTitle);

		if (announce && this.status) this.status.textContent = modeText.summary;
	}

	/**
	 * updateGallery - Swap in the server-rendered gallery for the selected variant
	 *
	 * Runs after the buy box has already updated, so a slow fetch never holds up the selection.
	 * Swapped directly rather than via `variant:update`, which would also drive any other variant
	 * listeners in the section. On failure the current gallery stays.
	 */
	updateGallery() {
		const variant = this.selectedVariant();
		if (!variant) return;

		this.galleryAbort?.abort();
		this.galleryAbort = null;
		if (variant.id === this.galleryVariantId) return;

		const abort = new AbortController();
		this.galleryAbort = abort;

		this.loadGallery(variant.id, abort.signal)
			.then(source => {
				// A newer selection owns the gallery now.
				if (abort.signal.aborted || !source) return;

				const live = this.dom.closest('.shopify-section')?.querySelector(VARIANT_GALLERY_SELECTOR);
				if (!live) return;

				live.replaceWith(document.importNode(source, true));
				this.galleryVariantId = variant.id;
				this.galleryAbort = null;
			})
			.catch(error => {
				if (!abort.signal.aborted) Logger.error(`${this.NAME} > gallery fetch failed`, error);
			});
	}

	/**
	 * loadGallery - The variant's server-rendered gallery, fetched once and shared by every caller
	 */
	loadGallery(variantId: number, signal?: AbortSignal): Promise<Element | null> {
		const cached = this.galleries.get(variantId);
		if (cached) return cached;

		const gallery = fetchVariantGalleryDocument({
			pageUrl: this.galleryPageUrl,
			sectionId: this.sectionId,
			variantId,
			signal,
		}).then(html => html.querySelector(VARIANT_GALLERY_SELECTOR));

		// A failed or aborted fetch mustn't poison the cache; the next selection retries.
		gallery.catch(() => {
			if (this.galleries.get(variantId) === gallery) this.galleries.delete(variantId);
		});
		this.galleries.set(variantId, gallery);

		return gallery;
	}

	/**
	 * initGalleryPrefetch - Warm a format's gallery when its card is hovered or focused
	 *
	 * Only on intent, never eagerly: most visitors never switch format, so warming every gallery
	 * up front would mostly fetch pages nobody sees.
	 */
	initGalleryPrefetch() {
		const prefetchCard = (event: Event) => {
			const card = event.target instanceof Element ? event.target.closest('.pdp-purchase__format') : null;
			const input = card?.querySelector<HTMLInputElement>('[data-pdp-format]');
			if (input) this.loadGallery(Number(input.value)).catch(() => {});
		};
		this.dom.querySelectorAll('.pdp-purchase__format').forEach(card => {
			card.addEventListener('pointerenter', prefetchCard);
		});
		this.dom.addEventListener('focusin', prefetchCard);
	}

	/**
	 * onPopState - Follow ?variant= when Back/Forward lands on another same-page entry
	 *
	 * syncUrl only rewrites the current entry, so an earlier one (before a #hash link, say) still
	 * names the old format, and traversing to it doesn't reload the page.
	 */
	onPopState = () => {
		const variantId = new URL(window.location.href).searchParams.get('variant') ?? this.defaultVariantId;
		const input = this.dom.querySelector<HTMLInputElement>(`[data-pdp-format][value="${variantId}"]`);
		if (!input || input.checked) return;

		input.checked = true;
		this.render({ announce: true });
		this.updateGallery();
	};

	/**
	 * syncUrl - Keep ?variant= shareable. Other params (`view`, UTMs) stay.
	 */
	syncUrl() {
		const variant = this.selectedVariant();
		if (!variant) return;

		const url = new URL(window.location.href);
		url.searchParams.set('variant', String(variant.id));
		history.replaceState(history.state, '', url);
	}

	/**
	 * initSticky - Show the sticky bar whenever the CTA is off screen (above or below), until the
	 * footer. While it shows, `pdp-sticky-bar-visible` on <body> lifts floating widgets clear of it.
	 */
	initSticky() {
		const { sticky, cta } = this;
		if (!sticky || !cta) return;

		const mobile = window.matchMedia(PHONE_MEDIA_QUERY);
		const footer = document.querySelector('.shopify-section-group-footer-group');
		let ctaVisible = true;
		let footerVisible = false;

		const update = () => {
			const visible = mobile.matches && !ctaVisible && !footerVisible;
			sticky.toggleAttribute('data-visible', visible);
			// Off-screen, its button must leave the tab order and the accessibility tree.
			sticky.inert = !visible;
			document.body.classList.toggle('pdp-sticky-bar-visible', visible);
		};

		const observer = new IntersectionObserver(entries => {
			entries.forEach(entry => {
				if (entry.target === cta) {
					ctaVisible = entry.isIntersecting;
				} else {
					footerVisible = entry.isIntersecting;
				}
			});
			update();
		});

		observer.observe(cta);
		if (footer) observer.observe(footer);
		mobile.addEventListener('change', update);

		// The bar grows when it shows an error, and the lifted widgets must clear it.
		new ResizeObserver(() => {
			document.documentElement.style.setProperty('--pdp-sticky-bar-height', `${sticky.offsetHeight}px`);
		}).observe(sticky);

		this.initStickyError();
	}

	/**
	 * initStickyError - Copy product-form.js's error into the sticky bar
	 *
	 * product-form.js shows, fills and times out one error element, beside the main CTA, which is off
	 * screen whenever the bar shows. Mirroring it keeps that element's timing the only timing.
	 */
	initStickyError() {
		const error = this.dom.querySelector<HTMLElement>('[data-pdp-error]');
		const stickyError = this.sticky?.querySelector<HTMLElement>('[data-pdp-sticky-error]');
		const stickyMessage = stickyError?.querySelector<HTMLElement>('[data-pdp-sticky-error-message]');
		if (!error || !stickyError || !stickyMessage) return;

		new MutationObserver(() => {
			setText(stickyMessage, error.textContent?.trim());
			stickyError.hidden = error.classList.contains('hidden');
		}).observe(error, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true });
	}

	onSubmit = () => {
		this.addRefused = false;
	};

	onCartError = (event: Event) => {
		const detail = (event as CustomEvent<{ sourceId?: string }>).detail;
		if (this.formComponent && detail?.sourceId === this.formComponent.id) this.addRefused = true;
	};

	/**
	 * onCartUpdate - Announce a successful add from this form
	 *
	 * product-form.js only announces success through an add-to-cart-component, which this block
	 * deliberately doesn't have (see blocks/pdp-purchase.liquid). A duplicate subscription still
	 * dispatches `cart:update` without `didError`, after its `cart:error`, so both are checked.
	 */
	onCartUpdate = (event: Event) => {
		const detail = (event as CustomEvent<{ data?: { didError?: boolean } }>).detail;
		if (detail?.data?.didError || this.addRefused || !this.status) return;

		this.status.textContent = 'Added to cart';
	};
}

/** Writes only on change, so unchanged text isn't re-announced or re-laid-out. */
const setText = (element: HTMLElement, text: string | undefined) => {
	const next = text ?? '';
	if (element.textContent !== next) element.textContent = next;
};

export default PdpPurchase;
